import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../core/config';
import { isValidCustomerId } from '../../../customers';
import { parseUpdateEvent } from '../../../l3-services/components/L3ServiceList/L3ServiceList.utils';
import { EventRegistrationScope, NetworkServiceUpdateEvent } from '../../../l3-services/model';
import { useNetworkServiceEventsApi } from '../../../l3-services/api/NetworkServiceEvents/NetworkServiceEvents.api';

export interface DashboardNetworkServiceEventsHookResult {
  maintenanceWorksUpdates: NetworkServiceUpdateEvent[];
  statusUpdates: NetworkServiceUpdateEvent[];
  incidentsUpdates: NetworkServiceUpdateEvent[];
}

const DASHBOARD_INITIALIZATION_DEBOUNCE_INTERVAL_IN_MILLISECONDS = 100;

export const useDashboardNetworkServiceEvents = (): DashboardNetworkServiceEventsHookResult => {
  const { sendJsonMessage, lastMessage, isConnected } = useNetworkServiceEventsApi();
  const { customerId } = useContext(AppContext);
  const [dashboardUpdateEvents, setDashboardUpdateEvents] = useState<DashboardNetworkServiceEventsHookResult>({
    maintenanceWorksUpdates: [],
    incidentsUpdates: [],
    statusUpdates: [],
  });

  useEffect(() => {
    if (isValidCustomerId(customerId)) {
      const customerL3ServicesEventRegistration = {
        eventRegistration: {
          customerId,
          eventRegistrationScopes: [
            EventRegistrationScope.Maintenance,
            EventRegistrationScope.Status,
            EventRegistrationScope.Notification,
          ],
        },
      };
      const timeoutId = setTimeout(
        () => {
          if (isConnected) {
            sendJsonMessage(customerL3ServicesEventRegistration)
          }
        },
        DASHBOARD_INITIALIZATION_DEBOUNCE_INTERVAL_IN_MILLISECONDS);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, isConnected]);

  useEffect(() => {
    if (lastMessage !== null) {
      const parsedEvents = parseUpdateEvent(lastMessage.data);
      if (parsedEvents) {
        setDashboardUpdateEvents(previousEvents => updateDashboardUpdateEvents(previousEvents, parsedEvents));
      }
    }
  }, [lastMessage]);

  return dashboardUpdateEvents;
};

const updateDashboardUpdateEvents = (
  previousEvents: DashboardNetworkServiceEventsHookResult,
  events: NetworkServiceUpdateEvent[],
): DashboardNetworkServiceEventsHookResult => {
  const maintenanceWorksUpdates = filterEventUpdatesByScope(events, EventRegistrationScope.Maintenance);
  const incidentsUpdates = filterEventUpdatesByScope(events, EventRegistrationScope.Notification);
  const statusUpdates = filterEventUpdatesByScope(events, EventRegistrationScope.Status);

  return {
    maintenanceWorksUpdates: maintenanceWorksUpdates.length
      ? maintenanceWorksUpdates
      : previousEvents.maintenanceWorksUpdates,
    incidentsUpdates: incidentsUpdates.length ? incidentsUpdates : previousEvents.incidentsUpdates,
    statusUpdates: statusUpdates.length ? statusUpdates : previousEvents.statusUpdates,
  };
};

const filterEventUpdatesByScope = (
  events: NetworkServiceUpdateEvent[],
  scope: EventRegistrationScope,
): NetworkServiceUpdateEvent[] => {
  return events
    .map(event => ({
      ...event,
      scopeUpdates: event.scopeUpdates.filter(scopeUpdate => scopeUpdate.scope === scope),
    }))
    .filter(event => Boolean(event.scopeUpdates.length));
};